import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Space, Button, Spin, Alert, Tag, Modal, InputNumber, Input, Tooltip } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconScale, IconRefresh, IconExternalLink } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getAdminDisputes, resolveAdminDispute, IAdminDispute } from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, numberSorter, dateSorter } from '../../../helpers/adminTableHelpers';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const UNDER_REVIEW_STATUS = 'UnderReview';

/**
 * The reviewer's queue: claims that got all the way through the funnel
 * without resolving themselves.
 *
 * This table should be nearly empty nearly all the time, and that is the
 * measure of whether the rest of the dispute system works -- the evidence,
 * the response deadline, the offers and the split rule exist precisely so
 * that almost nothing lands here (see BookingDispute's own doc comment on
 * the backend).
 *
 * Sorted oldest-first by default, because a queue ordered any other way is
 * one where the case that has been waiting longest keeps losing. Deciding
 * a claim opens next to the booking's own albaranes rather than from a row
 * here: the "Ver reserva" link is the important control on this screen, and
 * the inline decision modal is there for the case a reviewer has already
 * been through the evidence and is coming back to record the outcome.
 */
const DisputesSection: React.FC = () => {
  const { t } = useTranslation();
  const [disputes, setDisputes] = useState<IAdminDispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [deciding, setDeciding] = useState<IAdminDispute | undefined>(undefined);
  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminDisputes(UNDER_REVIEW_STATUS)
      .then(setDisputes)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openDecision = (dispute: IAdminDispute) => {
    setDeciding(dispute);
    // Pre-filled with the full claim so a reviewer who agrees with the
    // claimant doesn't have to retype it -- never a "split the difference"
    // default, which would be the tool nudging an outcome.
    setAmount(dispute.claimedAmount);
    setReason('');
  };

  const handleResolve = async () => {
    if (!deciding) return;
    if (!reason.trim()) {
      message.error(translate(t, TRANSLATIONS.ADMIN_DISPUTE_REASON_REQUIRED));
      return;
    }
    setSaving(true);
    try {
      await resolveAdminDispute(deciding.bookingId, amount ?? 0, reason.trim());
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setDeciding(undefined);
      load();
    } catch (e: any) {
      message.error(e.message || translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setSaving(false);
    }
  };

  // Shared by the desktop column and the mobile card. Still live, or closed
  // at nothing (a withdrawal, a claim that failed) -- in neither case is
  // there anything that should have moved, so this stays quiet rather than
  // raising a false alarm.
  const renderSettlement = (row: IAdminDispute): React.ReactNode => {
    if (!row.resolvedAmount) {
      return row.outcome ? <Tag>{translate(t, TRANSLATIONS.ADMIN_DISPUTES_SETTLEMENT_NOTHING)}</Tag> : null;
    }
    if (row.settledAt) return <Tag color="green">{translate(t, TRANSLATIONS.ADMIN_DISPUTES_SETTLEMENT_PAID)}</Tag>;
    return (
      <Tooltip title={row.settlementError}>
        <Tag color="red">{translate(t, TRANSLATIONS.ADMIN_DISPUTES_SETTLEMENT_STUCK)}</Tag>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: translate(t, TRANSLATIONS.ADMIN_DISPUTES_COLUMN_BOOKING),
      dataIndex: 'machineryTitle',
      key: 'machineryTitle',
      sorter: textSorter<IAdminDispute>('machineryTitle'),
      render: (title: string, row: IAdminDispute) => (
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          <Link to={`/heavy-duty-machinery/bookings/${row.bookingId}/dispute`}>
            <Space size={4}>
              <IconExternalLink size={13} />
              {translate(t, TRANSLATIONS.ADMIN_DISPUTES_OPEN_BOOKING)}
            </Space>
          </Link>
        </Space>
      ),
    },
    {
      title: translate(t, TRANSLATIONS.DISPUTE_CLAIMED_LABEL),
      dataIndex: 'claimedAmount',
      key: 'claimedAmount',
      sorter: numberSorter<IAdminDispute>('claimedAmount'),
      render: (value: number, row: IAdminDispute) => (
        <Text strong>
          {value.toFixed(2)} {row.currencyCode}
        </Text>
      ),
    },
    {
      title: translate(t, TRANSLATIONS.ADMIN_DISPUTES_COLUMN_AGE),
      dataIndex: 'daysOpen',
      key: 'daysOpen',
      sorter: numberSorter<IAdminDispute>('daysOpen'),
      // Age is the triage signal, so it reads as a warning once a claim has
      // been sitting here for a working week.
      render: (days: number) => (
        <Tag color={days >= 5 ? 'orange' : 'default'}>
          {translate(t, TRANSLATIONS.ADMIN_DISPUTES_DAYS, { days })}
        </Tag>
      ),
    },
    {
      title: translate(t, TRANSLATIONS.ADMIN_DISPUTES_COLUMN_MATERIAL),
      key: 'material',
      render: (_: unknown, row: IAdminDispute) => (
        <Space size={4}>
          <Tag>{translate(t, TRANSLATIONS.ADMIN_DISPUTES_EVIDENCE_COUNT, { count: row.evidenceCount })}</Tag>
          <Tag>{translate(t, TRANSLATIONS.ADMIN_DISPUTES_OFFER_COUNT, { count: row.offerCount })}</Tag>
        </Space>
      ),
    },
    {
      // Decided is not paid. Without this column a claim whose refund Stripe
      // refused sits in the closed list looking exactly like one that
      // settled cleanly, and the only way to find it is to go looking for
      // money that never arrived.
      title: translate(t, TRANSLATIONS.ADMIN_DISPUTES_COLUMN_SETTLEMENT),
      key: 'settlement',
      render: (_: unknown, row: IAdminDispute) => renderSettlement(row),
    },
    {
      title: translate(t, TRANSLATIONS.ADMIN_DISPUTES_COLUMN_FILED),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: dateSorter<IAdminDispute>('createdAt'),
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, row: IAdminDispute) => (
        <Button size="small" type="primary" onClick={() => openDecision(row)}>
          {translate(t, TRANSLATIONS.ADMIN_DISPUTES_DECIDE)}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          {translate(t, TRANSLATIONS.ADMIN_NAV_DISPUTES)}
        </Title>
        <Text type="secondary">{translate(t, TRANSLATIONS.ADMIN_DISPUTES_SUBTITLE)}</Text>
      </Space>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translate(t, TRANSLATIONS.ADMIN_DISPUTES_LOAD_ERROR)}
          action={
            <Button size="small" icon={<IconRefresh size={14} />} onClick={load}>
              {translate(t, TRANSLATIONS.ADMIN_DISPUTES_RETRY)}
            </Button>
          }
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : disputes.length === 0 ? (
        <EmptyState
          icon={<IconScale size={24} />}
          title={translate(t, TRANSLATIONS.ADMIN_DISPUTES_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_DISPUTES_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminDispute>
          rowKey="id"
          dataSource={disputes}
          columns={columns}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          scroll={{ x: true }}
          // The phone card carries what the desktop row plus its expanded
          // panel carry: the money, the age, and both accounts verbatim --
          // triage needs all three, and there is no "expand" on a card.
          toCard={(row) => ({
            key: String(row.id),
            title: row.machineryTitle,
            subtitle: (
              <Link to={`/heavy-duty-machinery/bookings/${row.bookingId}/dispute`}>
                <Space size={4}>
                  <IconExternalLink size={13} />
                  {translate(t, TRANSLATIONS.ADMIN_DISPUTES_OPEN_BOOKING)}
                </Space>
              </Link>
            ),
            badge: (
              <Tag color={row.daysOpen >= 5 ? 'orange' : 'default'} style={{ marginInlineEnd: 0 }}>
                {translate(t, TRANSLATIONS.ADMIN_DISPUTES_DAYS, { days: row.daysOpen })}
              </Tag>
            ),
            fields: [
              {
                label: translate(t, TRANSLATIONS.DISPUTE_CLAIMED_LABEL),
                value: `${row.claimedAmount.toFixed(2)} ${row.currencyCode}`,
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_DISPUTES_COLUMN_MATERIAL),
                value: (
                  <Space size={4} wrap>
                    <Tag style={{ marginInlineEnd: 0 }}>
                      {translate(t, TRANSLATIONS.ADMIN_DISPUTES_EVIDENCE_COUNT, { count: row.evidenceCount })}
                    </Tag>
                    <Tag style={{ marginInlineEnd: 0 }}>
                      {translate(t, TRANSLATIONS.ADMIN_DISPUTES_OFFER_COUNT, { count: row.offerCount })}
                    </Tag>
                  </Space>
                ),
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_DISPUTES_COLUMN_FILED),
                value: new Date(row.createdAt).toLocaleDateString(),
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_DISPUTES_COLUMN_SETTLEMENT),
                value: renderSettlement(row),
              },
              { label: translate(t, TRANSLATIONS.ADMIN_DISPUTES_CLAIMANT_SAYS), value: row.reason, wide: true },
              { label: translate(t, TRANSLATIONS.ADMIN_DISPUTES_RESPONDENT_SAYS), value: row.response, wide: true },
            ],
            actions: (
              <Button size="small" type="primary" onClick={() => openDecision(row)}>
                {translate(t, TRANSLATIONS.ADMIN_DISPUTES_DECIDE)}
              </Button>
            ),
          })}
          expandable={{
            // Both accounts, verbatim, without leaving the queue -- enough
            // to tell whether a case is worth opening next, never enough to
            // decide it from here.
            expandedRowRender: (row) => (
              <Space direction="vertical" size={8} style={{ maxWidth: 720 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {translate(t, TRANSLATIONS.ADMIN_DISPUTES_CLAIMANT_SAYS)}
                  </Text>
                  <Paragraph style={{ margin: 0 }}>{row.reason}</Paragraph>
                </div>
                {row.response && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {translate(t, TRANSLATIONS.ADMIN_DISPUTES_RESPONDENT_SAYS)}
                    </Text>
                    <Paragraph style={{ margin: 0 }}>{row.response}</Paragraph>
                  </div>
                )}
              </Space>
            ),
          }}
        />
      )}

      <Modal
        open={!!deciding}
        title={translate(t, TRANSLATIONS.ADMIN_DISPUTES_DECIDE)}
        onCancel={() => setDeciding(undefined)}
        onOk={handleResolve}
        confirmLoading={saving}
        okText={translate(t, TRANSLATIONS.ADMIN_DISPUTES_DECIDE)}
      >
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
          {translate(t, TRANSLATIONS.ADMIN_DISPUTES_DECIDE_HINT)}
        </Text>
        <Text style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
          {translate(t, TRANSLATIONS.DISPUTE_RESOLVED_LABEL)}
        </Text>
        <InputNumber
          min={0}
          max={deciding?.claimedAmount}
          step={10}
          value={amount}
          onChange={setAmount}
          addonAfter={deciding?.currencyCode}
          style={{ width: '100%', marginBottom: 14 }}
          disabled={saving}
        />
        <TextArea
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={translate(t, TRANSLATIONS.ADMIN_DISPUTES_REASON_PLACEHOLDER)}
          maxLength={2000}
          disabled={saving}
        />
      </Modal>
    </>
  );
};

export default DisputesSection;
